import { BrowserModule, Title } from "@angular/platform-browser";
import { NgModule, APP_INITIALIZER } from "@angular/core";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";

import { NzCollapseModule } from "ng-zorro-antd/collapse";
import { NzCheckboxModule } from "ng-zorro-antd/checkbox";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { registerLocaleData } from "@angular/common";
import en from "@angular/common/locales/en";
import zh from "@angular/common/locales/zh";
import { GlobalHeaderModule } from "./global-header/global-header.module";
import { StoreModule, ActionReducer } from "@ngrx/store";
import { appReducers } from "./store/reducers/app.reducer";
import { GlobalSubheaderModule } from "./global-subheader/global-subheader.module";
import { IconsModule } from "@va/core-library/icons";
import { LeftPanelComponent } from "./left-panel/left-panel.component";
import { environment } from "./../environments/environment";
import { AppState } from "./store/state/app.state";
import { ResearchGridPageModule } from "./research-grid-page/research-grid-page.module";
import { ChartsPageModule } from "./charts-page/charts-page.module";
import { EffectsModule } from "@ngrx/effects";
import { GlobalEffects } from "./store/effects/global.effects";
import { SharedModule } from "./shared/shared.module";
import { SectorsAndIndustriesPageModule } from "./sectors-and-industries-page/sectors-and-industries-page.module";
import { IndustryAnalysisPageModule } from "./sectors-and-industries-page/industry-analysis-page.module";
import { CompanyPageModule } from "./company-page/company-page.module";
import { CompsPageModule } from "./comps-page/comps-page.module";
import { ReportingPagesModule } from "./reporting-pages/reporting-pages.module";
import { WatchlistPageModule } from "./watchlist-page/watchlist-page.module";
import { GridWrapperStubComponent } from "./reporting-pages/grid-wrapper-stub-component";
import { StartupService } from "./startup.service";
import { RightPanelModule } from "./right-panel/right-panel.module";
import { AlertSettingsPageModule } from "./alert-settings-page/alert-settings-page.module";
// import { RightPanelComponent } from "./right-panel/right-panel.component";
// import { InferredDataSectionComponent } from "./shared/inferred-data-section/inferred-data-section.component";
import { SurprisePageModule } from "./surprise-page/surprise-page.module";
import { VSConsensusPageModule } from "./vsconsensus-page/vsconsensus-page.module";
import { RevisionPageModule } from "./revision-page/revision-page.module";
import { MexGridPageModule } from "./mex-grid-page/mex-grid-page.module";
import { QcDashboardPageModule } from "./qc-dashboard-page/qc-dashboard-page.module";
import { ResearchPageModule } from "./research-page/research-page.module";
import { DecoderPipe } from "./app.pipe";
import { AddinPageModule } from "./addin-page/addin-page.module";
import { AuthInterceptor } from "./interceptors/AuthInterceptor";
import { EmbargoManagementPageModule } from "./embargo-management-page/embargo-management-page.module";
import { ReportCenterSharedModule } from "./report-center-shared/report-center-shared.module";
import { ValuationPageModule } from "./valuation-page/valuation-page.module";
import { PharmaPageModule } from "./pharma-page/pharma-page.module";
import { TprModule } from "./tpr/tpr.module";
import { NzSkeletonModule } from "ng-zorro-antd/skeleton";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzMenuModule } from "ng-zorro-antd/menu";
import { NzPopoverModule } from "ng-zorro-antd/popover";
import { NzLayoutModule } from "ng-zorro-antd/layout";
import { NzMessageModule } from "ng-zorro-antd/message";
import { NzNotificationModule } from "ng-zorro-antd/notification";
import { NzDropDownModule } from "ng-zorro-antd/dropdown";
import { CompanyNavPageModule } from "./company-nav-page/company-nav-page.module";
import { TestingStubPageModule } from "./testing-stub-page/testing-stub-page.module";
// import { enUS } from "date-fns/locale"
// import { NZ_DATE_LOCALE } from "ng-zorro-antd/i18n";

import { ComparativeAnalysisModule } from "./comparative-analysis/comparative-analysis.module";

registerLocaleData(en);
registerLocaleData(zh);

export function logger(reducer: ActionReducer<AppState>): any {
  // default, no options
  return (obj, action: any): any => {
    const result = reducer(obj, action);
    return result;
  };
}

export const metaReducers = environment.production ? [] : [logger];

@NgModule({
  declarations: [
    AppComponent,
    LeftPanelComponent,
    GridWrapperStubComponent,
    DecoderPipe,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    NzLayoutModule,
    NzCollapseModule,
    NzCheckboxModule,
    NzSkeletonModule,
    NzInputModule,
    NzMenuModule,
    NzPopoverModule,
    NzMessageModule,
    NzNotificationModule,
    NzDropDownModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    GlobalHeaderModule,
    GlobalSubheaderModule,
    IconsModule,
    ResearchGridPageModule,
    ChartsPageModule,
    SectorsAndIndustriesPageModule,
    IndustryAnalysisPageModule,
    ComparativeAnalysisModule,
    SharedModule,
    CompanyPageModule,
    CompsPageModule,
    ReportingPagesModule,
    WatchlistPageModule,
    AlertSettingsPageModule,
    RightPanelModule,
    SurprisePageModule,
    VSConsensusPageModule,
    RevisionPageModule,
    MexGridPageModule,
    QcDashboardPageModule,
    ResearchPageModule,
    StoreModule.forRoot(appReducers, { metaReducers }),
    EffectsModule.forRoot([GlobalEffects]),
    AddinPageModule,
    EmbargoManagementPageModule,
    ReportCenterSharedModule,
    ValuationPageModule,
    PharmaPageModule,
    TprModule,
    CompanyNavPageModule,
    BrowserAnimationsModule,
    TestingStubPageModule,
  ],
  exports: [DecoderPipe],
  providers: [
    Title,
    {
      provide: APP_INITIALIZER,
      useFactory: (startupService: StartupService) => () =>
        startupService.getAppOktaUrls(),
      deps: [StartupService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    // {
    //   provide: NZ_DATE_LOCALE, useValue: enUS
    // }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
